import { decodeFunctionData, decodeFunctionResult, encodeFunctionData, keccak256, type Address, type Hex } from "viem";
import type { RedeemFeeRoute } from "./redeem-fee-quote";
import type { BootstrapResponse } from "../api/schemas";
import { ELYSIUM } from "../config/elysium";
import { isProductionRedeemRouteEnabled, observeRedeemDeployment } from "./redeem-fee-route";
import { encodeRedeemWithFeesCall, ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { contractReadError } from "../contracts/contract-errors";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadMethod, type RpcReadOptions } from "./read-rpc";
import type { TaskMintIdentity } from "../features/social-tasks/contracts/task-mint";
import { assertTaskMintCall, preflightTaskMint } from "../features/social-tasks/wallet/task-mint-preflight";
import { assertReferralMintCall, preflightReferralMint, type PreparedReferralMint } from "../features/referrals/wallet/referral-mint-preflight";
import { assertDirectRedeemCall, readElysiumRedeemPreview, recheckElysiumRedeemDeployment, type DirectRedeemIdentity } from "./elysium-redeem";
import { assertElysiumMintPermission, preflightElysiumMint } from "./elysium-mint";
import { abortableDelay } from "../api/abortable";
import { mintSnapshotSampledAt, refreshMintSnapshot } from "./mint-read-session";

export type NftPreflightOptions = RpcReadOptions & { onProgress?(message: string): void };

export type PreparedNftTransaction = {
  account: Address; chainId: number; to: Address; data: Hex; valueWei: bigint;
  /** Reviewed envelope signer, checked against the collection; never persisted. */
  mintSigner?: Address;
  /** Current in-memory deployment authority; never serialized into transaction history. */
  mintBootstrap?: () => BootstrapResponse | null;
  /** Mainnet fee getter can include wallet pricing on the pre-upgrade contract. */
  walletMintFee?: boolean;
  taskMint?: TaskMintIdentity & { sourceToken: Address };
  referralMint?: PreparedReferralMint;
  directRedeemIdentity?: DirectRedeemIdentity;
  redeemIdentity?: { tokenId: bigint; minPayoutWei: bigint; deadline: bigint; reviewedAt: bigint; route: RedeemFeeRoute;
    getBootstrap?: () => BootstrapResponse | null };
};

/** Reads/simulation only. The caller owns chain fencing and the actual send. */
export async function preflightNftTransaction(provider: Eip1193Provider, input: PreparedNftTransaction,
  assertContext: () => void, options: NftPreflightOptions = {}): Promise<() => void> {
  let directPreview: Awaited<ReturnType<typeof readElysiumRedeemPreview>> | undefined;
  if (input.chainId === ELYSIUM.chainId) {
    if (input.directRedeemIdentity) {
      const identity = assertDirectRedeemCall(input);
      directPreview = await readElysiumRedeemPreview(provider, { account: input.account, tokenId: identity.tokenId,
        minimum: identity.minPayoutWei, route: identity.route }, assertContext, options);
    } else assertElysiumMintPermission(input);
  } else if (input.directRedeemIdentity) throw new Error("DIRECT_REDEEM_CHAIN_MISMATCH");
  const call = decodeFunctionData({ abi: ponsRewardNftAbi, data: input.data });
  if (input.chainId === ELYSIUM.chainId && !input.directRedeemIdentity && call.functionName !== "mint")
    throw new Error("ELYSIUM_MINT_REVIEW_REQUIRED");
  if (input.referralMint) assertReferralMintCall(input);
  else if (input.taskMint) assertTaskMintCall(input);
  else if (call.functionName === "mint" && call.args[0].selectedTierUsd === 0n) {
    throw new Error("Unreviewed social task mint");
  }
  if (call.functionName !== "mint" && call.functionName !== "redeem" && call.functionName !== "redeemWithFees" && call.functionName !== "finalizeReward") {
    throw new Error("Unsupported NFT transaction");
  }
  if (call.functionName === "redeemWithFees") {
    const identity = input.redeemIdentity;
    const isolated = identity?.route.support === "ISOLATED_TEST_ONLY" && identity.route.environment !== "production";
    const production = identity && isProductionRedeemRouteEnabled(identity.route, identity.getBootstrap?.() ?? null);
    if (!identity || identity.route.kind !== "REDEEM_WITH_FEES_CURVE_V1" || !(isolated || production) || !identity.route.deploymentRevision.trim() ||
      identity.route.chainId !== input.chainId || identity.route.collection.toLowerCase() !== input.to.toLowerCase() ||
      identity.reviewedAt < 0n || identity.deadline !== identity.reviewedAt + 600n ||
      input.data.toLowerCase() !== encodeRedeemWithFeesCall(identity).toLowerCase()) {
      throw new Error("Unreviewed or unsupported redeemWithFees transaction");
    }
  } else if (input.redeemIdentity) throw new Error("Redemption route cannot fall back to another method");
  if (input.valueWei < 0n || input.valueWei >= (1n << 256n) ||
    call.functionName !== "mint" && input.valueWei !== 0n) throw new Error("Invalid NFT transaction value");

  const assertCurrent = () => {
    options.signal?.throwIfAborted(); assertContext();
    if (input.chainId === ELYSIUM.chainId && !input.directRedeemIdentity) assertElysiumMintPermission(input);
    if (input.directRedeemIdentity) assertDirectRedeemCall(input);
    const identity = input.redeemIdentity;
    if (identity?.route.support === "PINNED_RPC_READ_ONLY" &&
      !isProductionRedeemRouteEnabled(identity.route, identity.getBootstrap?.() ?? null)) {
      throw new Error("Redemption permission or deployment changed. Review again.");
    }
  };
  const request = async (method: RpcReadMethod, params: readonly unknown[]) => {
    assertCurrent();
    let result: unknown;
    try { result = await readRpc(provider, { method, params }, options); }
    catch (error) {
      assertCurrent();
      const call = method === "eth_call" ? params[0] as { to?: unknown; data?: unknown } : null;
      // Getter/other RPC failures do not establish the outcome of this mint.
      const collection = call?.to === input.to && call.data === input.data ? input.to : undefined;
      throw contractReadError(error, collection);
    }
    assertCurrent();
    return result;
  };
  const read = async (functionName: "currentMintFeeWei" | "mintFeeWeiFor" | "mintFeeWeiForTier" | "INSTANCE_ID" | "AUTHORIZATION_SIGNER" | "FEE_VAULT") => {
    const data = functionName === "mintFeeWeiForTier" && call.functionName === "mint" ?
      encodeFunctionData({ abi: ponsRewardNftAbi, functionName, args: [call.args[0].selectedTierUsd] }) : functionName === "mintFeeWeiFor" ?
      encodeFunctionData({ abi: ponsRewardNftAbi, functionName, args: [input.account] }) :
      encodeFunctionData({ abi: ponsRewardNftAbi, functionName });
    const result = await request("eth_call", [{ to: input.to, data }, "latest"]);
    if (typeof result !== "string" || !/^0x(?:[a-f0-9]{2})+$/i.test(result)) {
      throw new Error("The collection returned invalid preflight data");
    }
    return decodeFunctionResult({ abi: ponsRewardNftAbi, functionName, data: result as Hex });
  };

  // These independent reads share the same operation guard. Simulation and the
  // final chain/time checks still happen after every prerequisite has passed.
  let timeRequestedAt = mintSnapshotSampledAt(provider) ?? performance.now();
  const isElysiumMint = input.chainId === ELYSIUM.chainId && call.functionName === "mint";
  if (isElysiumMint) options.onProgress?.("Checking contract and mint authorization…");
  const [elysiumMint] = await Promise.all([
    isElysiumMint ? preflightElysiumMint(provider, input, assertCurrent, options) : Promise.resolve(null),
    isElysiumMint && input.taskMint ? preflightTaskMint(provider, input, assertCurrent, options) : Promise.resolve(),
    isElysiumMint && input.referralMint ? preflightReferralMint(provider, input, assertCurrent, options) : Promise.resolve(),
  ]);
  const [code, block, mint, redeemBinding] = await Promise.all([
    elysiumMint ? Promise.resolve(elysiumMint.code) : request("eth_getCode", [input.to, "latest"]),
    elysiumMint ? Promise.resolve(elysiumMint.block) : request("eth_getBlockByNumber", ["latest", false]),
    elysiumMint ? Promise.resolve(elysiumMint.mint) : call.functionName === "mint" ? Promise.all([
      input.taskMint || input.referralMint ? Promise.resolve(0n) : read(input.chainId === ELYSIUM.chainId ?
        "mintFeeWeiForTier" : input.walletMintFee ? "mintFeeWeiFor" : "currentMintFeeWei"),
      read("INSTANCE_ID"), read("AUTHORIZATION_SIGNER"),
    ]) : Promise.resolve(null),
    call.functionName === "redeemWithFees" ? Promise.all([read("FEE_VAULT"), read("INSTANCE_ID")]) : Promise.resolve(null),
  ]);
  assertCurrent();
  if (typeof code !== "string" || !/^0x(?:[a-f0-9]{2})+$/i.test(code)) {
    throw new Error("The configured collection has no contract code on this network");
  }
  if (input.referralMint && keccak256(code as Hex).toLowerCase() !== input.referralMint.evidence.collectionCodeHash.toLowerCase()) {
    throw new Error("Referral collection code differs from admitted deployment");
  }
  const timestamp = block && typeof block === "object" ? (block as Record<string, unknown>).timestamp : null;
  if (typeof timestamp !== "string" || !/^0x[0-9a-f]+$/i.test(timestamp)) {
    throw new Error("Wallet returned an invalid latest block timestamp");
  }
  let blockTime = BigInt(timestamp);
  const deadline = call.functionName === "mint" ? call.args[0].deadline :
    call.functionName === "redeem" || call.functionName === "redeemWithFees" ? call.args[2] : null;
  if (redeemBinding && input.redeemIdentity &&
    (redeemBinding[0].toString().toLowerCase() !== input.redeemIdentity.route.vault.toLowerCase() ||
      redeemBinding[1].toString().toLowerCase() !== input.redeemIdentity.route.instanceId.toLowerCase())) {
    throw new Error("Redemption deployment changed. Review again.");
  }
  const assertValidTime = () => {
    assertCurrent();
    const elapsed = BigInt(Math.floor(Math.max(0, performance.now() - timeRequestedAt) / 1_000));
    // Mint's review guard uses its BE response clock. The chain and monotonic
    // elapsed time remain authoritative here; a skewed PC clock grants nothing.
    if (deadline !== null && (blockTime + elapsed >= deadline ||
      call.functionName !== "mint" && BigInt(Math.floor(Date.now() / 1_000)) >= deadline)) {
      throw new Error("AuthorizationExpired: The deadline has expired. Prepare and review again.");
    }
  };
  assertValidTime();
  if (call.functionName === "mint") {
    const authorization = call.args[0];
    if (authorization.wallet.toLowerCase() !== input.account.toLowerCase()) throw new Error("AuthorizationWalletMismatch");
    if (input.chainId === ELYSIUM.chainId && blockTime < authorization.issuedAt) {
      console.warn("Mint RPC head behind authorization", { chainId: input.chainId,
        blockNumber: (block as { number?: unknown }).number, blockTimestamp: blockTime.toString(),
        issuedAt: authorization.issuedAt.toString(), lagSeconds: (authorization.issuedAt - blockTime).toString() });
      options.onProgress?.("Waiting for the network to reach the authorization time…");
      const waitUntil = performance.now() + 20_000;
      const expiryBudget = () => {
        const milliseconds = (authorization.deadline - blockTime) * 1_000n -
          BigInt(Math.ceil(Math.max(0, performance.now() - timeRequestedAt)));
        // Only convert the bounded timer duration, never the uint256 timestamp.
        return Number(milliseconds > 20_000n ? 20_000n : milliseconds < 0n ? 0n : milliseconds);
      };
      while (blockTime < authorization.issuedAt) {
        assertValidTime();
        const remaining = waitUntil - performance.now();
        if (remaining <= 0) throw new Error("AuthorizationNotYetValid: The network has not caught up. No transaction was sent. Check again shortly.");
        await abortableDelay(Math.min(2_000, remaining, Math.max(1, expiryBudget())), options.signal);
        assertValidTime();
        const readBudget = Math.min(waitUntil - performance.now(), expiryBudget());
        if (readBudget <= 0) continue;
        const requestedAt = performance.now();
        await refreshMintSnapshot(provider, readBudget);
        assertValidTime();
        const head = await readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] },
          { ...options, timeoutMs: Math.min(options.timeoutMs ?? 15_000, readBudget) });
        assertValidTime();
        const nextTime = head && typeof head === "object" ? (head as { timestamp?: unknown }).timestamp : null;
        if (typeof nextTime !== "string" || !/^0x[0-9a-f]+$/i.test(nextTime)) throw new Error("Invalid mint RPC block timestamp");
        blockTime = BigInt(nextTime); timeRequestedAt = mintSnapshotSampledAt(provider) ?? requestedAt;
        assertValidTime();
      }
      options.onProgress?.("Checking mint details and simulating the transaction…");
    }
    if (blockTime < authorization.issuedAt) throw new Error("AuthorizationNotYetValid");
    if (input.valueWei !== authorization.quotedMintFeeWei) throw new Error("IncorrectMintFee");
    if (mint?.[0] !== input.valueWei) {
      throw new Error("StaleQuotedMintFee: The mint fee changed. Prepare and review a new quote.");
    }
    if (String(mint[1]).toLowerCase() !== authorization.instanceId.toLowerCase()) {
      throw new Error("InvalidAuthorizationInstance");
    }
    if (!input.mintSigner || String(mint[2]).toLowerCase() !== input.mintSigner.toLowerCase()) {
      throw new Error("The authorization signer does not match the collection");
    }
  }

  // Independent read-only checks share the same guard. Simulation must still
  // wait for every check; overlapping them does not grant earlier send permission.
  if (call.functionName === "mint") options.onProgress?.("Checking contract and mint authorization…");
  const [recheckElysium] = await Promise.all([
    Promise.resolve(elysiumMint?.recheck),
    !isElysiumMint && input.taskMint ? preflightTaskMint(provider, input, assertCurrent, options) : Promise.resolve(),
    !isElysiumMint && input.referralMint ? preflightReferralMint(provider, input, assertCurrent, options) : Promise.resolve(),
  ]);
  if (call.functionName === "mint") options.onProgress?.("Simulating your mint transaction…");
  const result = await request("eth_call", [{ from: input.account, to: input.to, data: input.data,
    value: `0x${input.valueWei.toString(16)}` }, "latest"]);
  if (typeof result !== "string" || !/^0x(?:[a-f0-9]{2})*$/i.test(result)) throw new Error("Invalid transaction simulation response");
  // Empty return data is valid for both redeem methods, but not mint/finalize. No-code calls
  // cannot masquerade as a successful result for these value-returning methods.
  decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: call.functionName, data: result as Hex });
  if (input.redeemIdentity?.route.support === "PINNED_RPC_READ_ONLY") {
    const header = await request("eth_getBlockByNumber", ["latest", false]);
    const hash = header && typeof header === "object" ? (header as Record<string, unknown>).hash : null;
    if (typeof hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(hash) || BigInt(hash) === 0n) {
      throw new Error("Invalid redemption deployment block");
    }
    const observed = await observeRedeemDeployment(provider, input.to, { blockHash: hash as Hex, requireCanonical: true }, assertCurrent, options);
    if (JSON.stringify(observed) !== JSON.stringify(input.redeemIdentity.route.observedDeployment)) {
      throw new Error("Redemption implementation changed. Review again.");
    }
  }
  if (call.functionName === "mint") options.onProgress?.("Verifying the latest contract state before opening your wallet…");
  await refreshMintSnapshot(provider);
  assertValidTime();
  await Promise.all([
    input.referralMint?.evidence.verifyDeployment(provider, assertCurrent, options),
    directPreview ? recheckElysiumRedeemDeployment(provider, directPreview, assertCurrent, options) : undefined,
    recheckElysium?.(),
  ]);
  assertValidTime();
  return assertValidTime;
}
